import React from 'react';

import styled from 'styled-components';

import { APIDocumentSearchResult } from '@cd3p/core/types/api';
import { GlobalLoader, Stack, Typography } from 'components';

import { useTranslation } from 'third-party';

import { themeConfig } from 'styles/theme';

const Content = styled(Stack)`
  padding: 0.8rem;
  gap: 0.8rem;
  flex-grow: 1;
  min-width: 0;
  border-bottom: 1px solid ${props => props.theme.custom.palette.muted100};
  overflow-y: scroll;
`;

const Title = styled(Typography)`
  font-size: 1.4rem;
  font-weight: 600;
  color: ${props => props.theme.custom.palette.bluegray500};
`;

const SearchItem = styled(Stack)`
  padding: 0 0.8rem;
  cursor: pointer;
  min-width: 0;
`;

const SearchItemTitle = styled(Typography)`
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 2.4rem;
  color: ${props => props.theme.custom.palette.darkText};
  padding-bottom: 2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const SearchItemText = styled(Typography)`
  font-weight: 600;
  font-size: 1.2rem;
  line-height: 1.8rem;
  color: ${props => props.theme.custom.palette.darkText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const EmptyState = styled(Typography)`
  margin-top: 3rem;
  align-self: center;
  font-size: 1.6rem;
  font-weight: 600;
  color: ${props => props.theme.custom.palette.muted800};
`;

export const DIALOG_ITEM_HEIGHT = 85;
export const CONTRACTOR_DIALOG_ITEM_HEIGHT = 65;

type SearchResultsProps = {
  onSelectItem?: (item: APIDocumentSearchResult) => void;
  searchData?: APIDocumentSearchResult[];
  searchKeyword: string;
  searchLoading?: boolean;
};

const getHighlightedKeywordContent = (
  originalValue: string[] | null,
  keyword: string,
) => {
  if (!keyword || keyword.length === 0 || !originalValue) {
    return originalValue;
  }
  const JOIN_SEPARATOR = '... ';

  const keywordToFindRegex = new RegExp(`<em>(${keyword})</em>`, 'gi');
  const partsToHighlight = originalValue
    .join(JOIN_SEPARATOR)
    .split(keywordToFindRegex);
  const reactElements = partsToHighlight.map((text, index) => {
    if (keyword.toLowerCase() !== text.toLowerCase()) {
      return text;
    } else {
      return (
        <em
          key={index}
          style={{ backgroundColor: themeConfig.custom.palette.searchResult }}
        >
          {text}
        </em>
      );
    }
  });
  return <>{reactElements.flat()}</>;
};

export const SearchResults = ({
  searchData,
  onSelectItem,
  searchKeyword,
  searchLoading,
}: SearchResultsProps) => {
  const { t } = useTranslation();
  return searchLoading ? (
    <GlobalLoader />
  ) : (
    <Content>
      <Title variant="body2">Results</Title>
      {!searchData?.length && (
        <EmptyState variant="h6">
          {t('helpCenter.panel.noMatchingResults')}
        </EmptyState>
      )}
      {searchData?.map(item => {
        return (
          <SearchItem
            key={item.document.id}
            onClick={() => onSelectItem?.(item)}
          >
            <SearchItemTitle variant="body2">
              {item.highlights?.title
                ? getHighlightedKeywordContent(
                    item.highlights.title,
                    searchKeyword,
                  )
                : item.document.title}
            </SearchItemTitle>
            <SearchItemText variant="body2">
              {item.highlights?.optimizedSearchText
                ? getHighlightedKeywordContent(
                    item.highlights.optimizedSearchText,
                    searchKeyword,
                  )
                : item.document.optimizedSearchText}
            </SearchItemText>
          </SearchItem>
        );
      })}
    </Content>
  );
};
